import { Component, inject, OnInit, OnDestroy, signal, computed, effect } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subject, catchError, finalize, map, of, takeUntil, throwError } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import {
  DynamicFilterConfig,
  DynamicFilterValues,
  EnumValues,
  FilterOption,
  GlobalEntityDefinition,
  createDynamicFilterState,
} from '@card-software/library';
import { AuthService, debouncedServerSearch } from '@card-software/nexus-utilities';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import _ from 'lodash';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { PaginatorState } from 'primeng/paginator';
import { EnumService } from '../../../services/enum.service';
import { FinishedGoodsTransfer, FinishedGoodsTransferSearchCommand } from '../../../models/inventory/finished-goods-transfer';
import { ProductionCard } from '../../../models/production/production-card';
import { UserGroup } from '../../../models/authorization/enum/user-group.enum';
import { ProductionCardsProxyService } from '../../../proxies/production/production-card.proxy.service';
import { FinishedGoodsTransferProxyService } from '../../../proxies/inventory/finished-goods-transfer.proxy.service';

@Component({
  selector: 'app-finished-good-transfers',
  templateUrl: './finished-good-transfers.component.html',
  styleUrl: './finished-good-transfers.component.scss',
})
export class FinishedGoodTransfersComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private enumService = inject(EnumService);
  private translateService = inject(TranslateService);
  private readonly productionCardProxyService = inject(ProductionCardsProxyService);
  private readonly finishedGoodsTransferProxyService = inject(FinishedGoodsTransferProxyService);

  private destroy$ = new Subject<void>();
  private deepLinkHandled = false;
  private appliedFilters: DynamicFilterValues = {};
  isLoading = signal<number>(0);
  showFilters = signal<boolean>(true);
  finishedGoodsTransfers = signal<FinishedGoodsTransfer[]>([]);
  filterConfig = signal<DynamicFilterConfig | null>(null);
  readonly filterState = createDynamicFilterState({ storageKey: 'finished-goods-transfers-filters', config: this.filterConfig });

  listProductionCards = signal<ProductionCard[]>([]);
  filteredProductionCards = signal<ProductionCard[]>([]);

  finishedGoodsTransferStatus = signal<EnumValues>({});

  // Static status options (fallback if enum service doesn't have them)
  statusOptions = computed<FilterOption[]>(() => {
    const statuses = this.finishedGoodsTransferStatus();
    if (Object.keys(statuses).length > 0) {
      return Object.keys(statuses)
        .filter((key) => !isNaN(Number(key)))
        .map((key) => ({
          value: Number(key),
          label: statuses[Number(key)],
        }));
    }
    // Fallback to hardcoded values if enum not available
    return [
      { value: 0, label: 'Draft' },
      { value: 1, label: 'Ready For Transfer' },
      { value: 2, label: 'In Route' },
      { value: 3, label: 'Received' },
    ];
  });

  // Pagination
  currentPage = 1;
  pageSize = 25;
  totalRecords = 0;

  canEdit = computed(() => {
    if (AuthService.hasRole(UserGroup.FinishedGoodsTransferEditor) || AuthService.hasRole(UserGroup.FinishedGoodsTransferAdmin)) {
      return true;
    }
    if (AuthService.hasRole(UserGroup.FinishedGoodsTransferViewer) || AuthService.hasRole(UserGroup.FinishedGoodsTransferReceiver)) {
      return false;
    }
    return false;
  });

  getRowData(rowData: unknown): FinishedGoodsTransfer {
    return rowData as FinishedGoodsTransfer;
  }

  constructor() {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => {
      if (!this.deepLinkHandled) {
        this.deepLinkHandled = true;
        const productionCardNumber = this.route.snapshot.queryParamMap.get('productionCardNumber');
        if (productionCardNumber) {
          this.applyProductionCardFromUrl(productionCardNumber, filters);
          return;
        }
      }
      this.searchWithFilters(filters);
    });

    effect(
      () => {
        const statusesValue = this.enumService.getEnum('FinishedGoodTransferStatus', 'inventory').values;
        if (Object.keys(statusesValue).length > 0) {
          this.finishedGoodsTransferStatus.set(statusesValue);
        }
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        if (this.translateService.currentLang) {
          this.tryInitializeFilterConfig();
        }
      },
      { allowSignalWrites: true },
    );
  }

  ngOnInit(): void {
    // The dynamic-filters panel renders the autocomplete, so there is no element for
    // ServerSearchDirective: the shared operator is wired by hand and released with the component.
    this.productionCardQueries$
      .pipe(
        debouncedServerSearch((query) => this.searchProductionCards(query)),
        takeUntil(this.destroy$),
      )
      .subscribe((cards) => this.filteredProductionCards.set(cards));

    this.loadInitialData();

    this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.tryInitializeFilterConfig();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getTransferStrict(transfer: unknown): FinishedGoodsTransfer {
    return transfer as FinishedGoodsTransfer;
  }

  loadInitialData(): void {
    // Initial search is handled by the filter state
  }

  private tryInitializeFilterConfig(): void {
    if (this.translateService.currentLang) {
      this.initializeFilterConfig();
    }
  }

  private initializeFilterConfig(): void {
    const translate = (key: string, fallback: string) => {
      const translation = this.translateService.instant(key);
      return translation === key ? fallback : translation;
    };

    const config: DynamicFilterConfig = {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'searchByNumber',
          label: translate('Finished_Goods_Transfer_Number', 'Finished Goods Transfer Number'),
          placeholder: translate('Enter_finished_goods_transfer_number', 'Enter finished goods transfer number'),
          defaultVisible: true,
        },
        {
          type: 'multiselect',
          formControlName: 'status',
          label: translate('Status', 'Status'),
          placeholder: translate('Select_status', 'Select status'),
          options: this.statusOptions(),
          showClear: true,
          defaultVisible: true,
        },
        {
          type: 'autocomplete',
          formControlName: 'productionCard',
          label: translate('Production_Card', 'Production Card'),
          placeholder: translate('Select_production_card', 'Select production card'),
          autocompleteConfig: {
            suggestions: this.filteredProductionCards(),
            optionLabel: 'productionCardNumber',
            optionValue: 'id',
            completeMethod: (event: AutoCompleteCompleteEvent) => this.productionCardQueries$.next(event.query),
            resolveById: (id: string) => this.resolveProductionCard(id),
            dropdown: true,
          },
          showClear: true,
          defaultVisible: false,
        },
      ],
    };

    this.filterConfig.set(config);
  }

  private resolveProductionCard(id: string): Observable<ProductionCard | null> {
    return this.productionCardProxyService.getById(id).pipe(
      map((response) => response.data ?? null),
      catchError((error: HttpErrorResponse) => (error.status === 404 ? of(null) : throwError(() => error))),
    );
  }

  /** Deep link from Production.UI's production card details; overrides the persisted production card filter. */
  private applyProductionCardFromUrl(productionCardNumber: string, applied: DynamicFilterValues): void {
    this.startLoading();
    this.productionCardProxyService
      .getByProductionCardNumber(productionCardNumber)
      .pipe(
        finalize(() => this.stopLoading()),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (response) => {
          const productionCard = response?.data;
          if (!productionCard) {
            this.searchWithFilters(applied);
            return;
          }
          this.filterState.apply({ ...applied, productionCard });
        },
        error: () => this.searchWithFilters(applied),
      });
  }

  private startLoading(): void {
    this.isLoading.update((val) => val + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((val) => val - 1);
  }

  private readonly productionCardQueries$ = new Subject<string>();

  private searchProductionCards(query: string): Observable<ProductionCard[]> {
    return this.productionCardProxyService.search({ productionCardNumber: query }).pipe(
      finalize(() => this.stopLoading()),
      map((result) => result?.data ?? []),
    );
  }

  search() {
    const formValues = this.appliedFilters;
    const searchTerm = (formValues['searchByNumber'] as string | undefined)?.trim();
    const productionCard = formValues['productionCard'] as string | { id?: string } | null | undefined;
    const productionCardId = typeof productionCard === 'string' ? productionCard : productionCard?.id;
    const statusesRaw = formValues['status'];
    const statuses = Array.isArray(statusesRaw)
      ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
        statusesRaw.map((s: any) => (typeof s === 'object' && s.value !== undefined ? s.value : s))
      : [];

    const command: FinishedGoodsTransferSearchCommand = {
      statuses: statuses && statuses.length > 0 ? statuses : undefined,
      searchTerm: searchTerm || undefined,
      initiator: productionCardId
        ? {
            entityDefinitionCode: GlobalEntityDefinition.ProductionCard,
            reference: productionCardId,
            number: '',
          }
        : undefined,
    };

    this.startLoading();
    this.finishedGoodsTransferProxyService
      .search(command, this.currentPage, this.pageSize)
      .pipe(
        finalize(() => {
          this.stopLoading();
        }),
      )
      .subscribe({
        next: (result) => {
          this.finishedGoodsTransfers.set(result?.data?.items || []);
          this.totalRecords = result?.data?.totalCount || 0;
        },
        error: () => {
          this.finishedGoodsTransfers.set([]);
          this.totalRecords = 0;
        },
      });
  }

  onPageChange(event: PaginatorState) {
    this.currentPage = (event.page || 0) + 1;
    this.pageSize = event.rows || this.pageSize;
    this.search();
  }

  private searchWithFilters(filters: DynamicFilterValues): void {
    this.appliedFilters = filters;
    this.currentPage = 1;
    this.search();
  }

  redirectToDetails(id?: string): void {
    const targetId = id ?? 'new';
    this.router.navigate(['finished-good-transfer-details'], { queryParams: { id: targetId } });
  }

  getRollNumbers(transfer: FinishedGoodsTransfer): string {
    return transfer.rollNumbers?.join(', ') || '';
  }

  getStatusLabel(status: number): string {
    const statusEnum = this.finishedGoodsTransferStatus();
    return statusEnum[status] || status.toString();
  }
}
